/**
 * Backend API klienti. Token localStorage da (Sanctum personal access token).
 * Server yo'q bo'lsa `ApiError` (status 0) — UI demo rejimga o'tishi mumkin.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const TOKEN_KEY = 'muslab.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts: { raw?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : 'network error');
  }
  if (opts.raw) return res as unknown as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'message' in data
        ? String((data as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

// ─── Tiplar (backend bilan shartnoma) ──────────────────────────────────────

export type Role = 'superadmin' | 'institution_admin' | 'teacher' | 'student';

export interface User {
  id: number;
  name: string;
  email: string | null;
  phone?: string | null;
  role: Role;
  institution_id: number | null;
  institution?: { id: number; name: string } | null;
}

export interface Group {
  id: number;
  name: string;
  code: string;
  department_id: number;
  department?: { id: number; name: string; faculty?: { id: number; name: string } };
  students_count?: number;
  /** O'qituvchi uchun: shu guruhdagi mening topshiriqlarim soni */
  my_assignments_count?: number;
}

export interface LabSummary {
  id: number;
  slug: string;
  version: number;
  unit: string;
  title: Record<string, string>;
  definition?: unknown;
}

export interface Assignment {
  id: number;
  lab_id: number;
  lab: LabSummary;
  group_id: number;
  group?: Group;
  teacher_id: number;
  deadline: string | null;
  max_attempts: number;
  created_at: string;
  /** Talaba uchun */
  my_sessions?: SessionSummary[];
  /** O'qituvchi uchun */
  stats?: { students: number; submitted: number; graded: number; avg_score: number | null };
}

export interface SessionSummary {
  id: number;
  assignment_id: number;
  student_id: number;
  student?: { id: number; name: string; email: string };
  seed: string;
  attempt: number;
  status: 'in_progress' | 'submitted' | 'graded';
  score: number | null;
  max_score: number | null;
  passed: boolean | null;
  started_at: string;
  submitted_at: string | null;
  duration_seconds: number | null;
}

export interface SessionDetail extends SessionSummary {
  actions: unknown[];
  journal: unknown[];
  answers: Record<string, unknown> | null;
  conclusion: string | null;
  grade: unknown | null;
  teacher_comment: string | null;
  lab: LabSummary & { definition: unknown };
}
